import type { ReactNode } from 'react'
import { NavLink } from 'react-router'

/**
 * sidebar-item — Figma `131:8787`, DESIGN-HANDOVER.md §5.10 / §8.1.
 *
 * A 220×32 row: icon, then label. Four states, every value MEASURED from the node's own bound
 * variables (re-pulled 2026-08-27, not copied from the handover prose, which had two of them
 * wrong):
 *
 *   Normal    white fill, no border
 *   Hover     `#f4fafe` — `Colors/Surface/Secondary/Default`, node `131:8788`
 *   Pressed   `#ecf5fe` — `Colors/Surface/Secondary/Hover`, node `131:8792`
 *   Selected  `#f4fafe` fill + `#bdc6f7` border, radius 6
 *
 * Two of those carry a trap worth stating rather than silently encoding:
 *
 * 1. **Hover and Selected share the same fill.** Selected is distinguishable ONLY by its
 *    `#bdc6f7` border. That is what the file draws; it is likely an authoring artifact and is
 *    worth raising with the design source, but it is not this component's place to "fix" by
 *    inventing a different hover tint.
 * 2. **The Pressed token is named for a different state.** `#ecf5fe` lives under
 *    `Surface/Secondary/Hover`, yet the node that binds it is the PRESSED variant. The value is
 *    firm; the name is misleading. Do not "correct" this to `surface-secondary` on the strength
 *    of the token name alone.
 *
 * The focus ring reuses `border-secondary-hover` (`#858ff0`). ⚠ This is a COMPOUNDED inference:
 * DESIGN-HANDOVER.md:454 marks that token an inference for the CHECKBOX only, and :147 scopes it
 * as a "checkbox hover/focus-ring candidate". No focus state is drawn anywhere in the shell
 * evidence. It is used here because shipping a keyboard-navigable nav with NO visible focus is
 * worse than reusing the one plausible token — but it is a guess, recorded as one.
 */
export function SidebarItem({
  to,
  icon,
  label,
  selected,
  onClick,
  trailing,
}: {
  /** Omit for a non-navigating row (a disclosure header, say) — it renders as a <button>. */
  to?: string
  icon: ReactNode
  label: string
  /** Force the selected look. Omit when `to` is given: NavLink derives it from the route. */
  selected?: boolean
  onClick?: () => void
  /** Optional right-aligned slot (a chevron, a count). */
  trailing?: ReactNode
}) {
  const base =
    // MEASURED against node `131:8800` (re-pulled 2026-08-29 after the owner reported the sidebar
    // did not match the design): `p-8` all round, `gap-6`, `rounded-6`, label Inter **Medium
    // 12px/15px in BLACK**. What shipped was `px-6` with `gap-4` and a hard `h-8` — the padding was
    // 2px short on each side and the icon-label gap 2px short, which is why the rows read tighter
    // and smaller than the frame. Height is now driven by the padding (8 + 16 + 8 = 32px), which is
    // what the node does, instead of being pinned by `h-8`.
    'flex w-full items-center gap-small-medium rounded-150 border p-medium text-left text-body-default-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-secondary-hover'

  // Selected carries the border; every other state is borderless. `border-transparent` (not
  // `border-0`) keeps the box the same size in all four states, so nothing shifts by 1px when a
  // row becomes selected.
  const rest =
    'border-transparent bg-surface-white text-text-default hover:bg-surface-secondary active:bg-surface-secondary-hover'
  const on = 'border-border-secondary bg-surface-secondary text-text-default'

  const content = (
    <>
      <span aria-hidden className="flex h-4 w-4 shrink-0 items-center justify-center">
        {icon}
      </span>
      <span className="truncate">{label}</span>
      {trailing ? <span className="ml-auto flex shrink-0 items-center">{trailing}</span> : null}
    </>
  )

  if (to) {
    return (
      <NavLink
        to={to}
        // `end` so `/campaigns` does not stay highlighted while sitting on
        // `/campaigns/:id` — a parent route matching its children is React Router's
        // default and reads as the wrong row being active.
        end
        className={({ isActive }) => `${base} ${isActive || selected ? on : rest}`}
      >
        {content}
      </NavLink>
    )
  }

  return (
    <button
      type="button"
      onClick={onClick}
      /*
       * `aria-current` must be set EXPLICITLY on this branch. The `to` branch above gets it free
       * from `NavLink`, so the nav rows satisfied the keyboard/ARIA contract by accident — but a
       * Favorites row has no `to` (it writes `?brandId=` instead), so it renders here, and this
       * branch was applying the selected STYLING while telling assistive tech nothing. A sighted
       * user could see which brand was active and a screen-reader user could not.
       * Found by /design-change-review, 2026-08-28.
       */
      {...(selected ? { 'aria-current': 'page' as const } : {})}
      className={`${base} ${selected ? on : rest}`}
    >
      {content}
    </button>
  )
}
